import * as React from 'react';
import {
  Box,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';

export default function DatePicker({
  onChange,
  minYear,
  maxYear,
  restrict = "none",
  defaultValue,
  monthFormat = "full",
  size = "medium",
  label = "Date",
  order = ["month", "day", "year"],
  gap = 2,
  controlWidths = { month: 4, day: 4, year: 4 },
  placeholders = { month: "Month", day: "Day", year: "Year" },
}) {
  const [month, setMonth] = React.useState('');
  const [day, setDay] = React.useState('');
  const [year, setYear] = React.useState('');

  const currentYear = new Date().getFullYear();

  React.useEffect(() => {
    if (defaultValue === "") {
      setYear('');
      setMonth('');
      setDay('');
    } else if (defaultValue) {
      const dateObj =
        typeof defaultValue === "string" ? new Date(defaultValue) : defaultValue;
      if (!isNaN(dateObj)) {
        setYear(dateObj.getFullYear());
        setMonth(dateObj.getMonth() + 1);
        setDay(dateObj.getDate());
      }
    }
  }, [defaultValue]);

  // Year range logic
  let computedMinYear = minYear ?? 1900;
  let computedMaxYear = maxYear ?? currentYear;
  if (restrict === "past") {
    computedMaxYear = maxYear ?? currentYear;
  } else if (restrict === "future") {
    computedMinYear = minYear ?? currentYear;
    computedMaxYear = maxYear ?? currentYear + 100;
  }
  const years = Array.from(
    { length: computedMaxYear - computedMinYear + 1 },
    (_, i) => computedMinYear + i
  );

  const monthsFull = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];
  const monthsShort = [
    'Jan','Feb','Mar','Apr','May','Jun',
    'Jul','Aug','Sep','Oct','Nov','Dec'
  ];
  const months = monthFormat === "short" ? monthsShort : monthsFull;

  const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);
  const getDaysInMonth = (m, y) => {
    if (!m) return 31;
    switch (m) {
      case 2: return isLeapYear(y) ? 29 : 28;
      case 4: case 6: case 9: case 11: return 30;
      default: return 31;
    }
  };
  const days = Array.from({ length: getDaysInMonth(month, year) }, (_, i) => i + 1);

  React.useEffect(() => {
  if (month && day && year) {
    const dateObj = new Date(year, month - 1, day);

    // Manual formatting instead of toISOString()
    const formatted = [
      dateObj.getFullYear(),
      String(dateObj.getMonth() + 1).padStart(2, '0'),
      String(dateObj.getDate()).padStart(2, '0')
    ].join('-');

    onChange?.({ dateObj, formatted });
  } else {
    onChange?.(null);
  }
}, [month, day, year, onChange]);

  const selectSize = size === "small" ? "small" : "medium";
  const customStyle = size === "large" ? { fontSize: "1.25rem", padding: "12px" } : {};

  const renderControl = (type) => {
    const width = controlWidths[type] ?? 4;
    switch (type) {
      case "month":
        return (
          <Grid key="month">
            <Select
              value={month || ""}
              onChange={(e) => setMonth(e.target.value)}
              fullWidth
              size={selectSize}
              sx={customStyle}
              displayEmpty
              renderValue={(selected) =>
                selected === "" ? <span style={{ color: '#999' }}>{placeholders.month}</span> : months[selected - 1]
              }
            >
              {months.map((m, idx) => (
                <MenuItem key={m} value={idx + 1}>{m}</MenuItem>
              ))}
            </Select>
          </Grid>
        );
      case "day":
        return (
          <Grid key="day">
            <Select
              value={day || ""}
              onChange={(e) => setDay(e.target.value)}
              fullWidth
              size={selectSize}
              sx={customStyle}
              displayEmpty
              renderValue={(selected) =>
                selected === "" ? <span style={{ color: '#999' }}>{placeholders.day}</span> : selected
              }
            >
              {days.map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </Select>
          </Grid>
        );
      case "year":
        return (
          <Grid key="year">
            <Select
              value={year || ""}
              onChange={(e) => setYear(e.target.value)}
              fullWidth
              size={selectSize}
              sx={customStyle}
              displayEmpty
              renderValue={(selected) =>
                selected === "" ? <span style={{ color: '#999' }}>{placeholders.year}</span> : selected
              }
            >
              {years.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </Grid>
        );
      default:
        return null;
    }
  };

  const isPartialSelection =
    (month || day || year) && !(month && day && year);

  return (
    <Box>
      <FormLabel sx={{ fontSize: '0.95rem', color: 'text.secondary' }}>
        {label}
      </FormLabel>
      <Grid container spacing={gap}>
        {order.map((field) => renderControl(field))}
      </Grid>
      {isPartialSelection && (
        <Typography variant="body2" color="warning" sx={{ mt: 0.5 }}>
          Please complete selection of day, month, and year.
        </Typography>
      )}
    </Box>
  );
}
